import { UseFormSetValue } from 'react-hook-form';
import { ClassifierData } from 'types/classifiers';
import { HistoryEntityDto, HistoryForm, UpdateHistoryDto } from 'types/histories';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { HistoryFormData } from './HistoryModal';
import HistoryRecordContent from './HistoryRecordContent';
import { useHistoryRecord } from './useHistoryRecord';

interface Props {
    index: number;
    history: HistoryForm;
    allClassifiers: ClassifierData[];
    classifierCode: string | null;
    setValue: UseFormSetValue<HistoryFormData>;
    hasUserEditPermission: boolean;
    isLatestRecord: boolean;
    handleUpdate: (
        record: HistoryEntityDto & UpdateHistoryDto & { entityType?: string; fieldType?: string }
    ) => Promise<void>;
    handleDelete?: (historyEntity: HistoryEntityDto) => Promise<void>;
    allHistories: HistoryForm[];
}

const HistoryRecord: React.FC<Props> = ({
    index,
    history,
    setValue,
    classifierCode,
    allClassifiers,
    hasUserEditPermission,
    isLatestRecord,
    handleUpdate,
    handleDelete,
    allHistories,
}) => {
    const { mapToUpdateHistoryDto, recordState, updateRecordState } = useHistoryRecord({ history, index, setValue });

    const handleSaveHistoryRecord = async ({
        startDate,
        endDate,
    }: Pick<HistoryForm, 'startDate' | 'endDate'>): Promise<void> => {
        try {
            const dto = mapToUpdateHistoryDto(startDate, endDate);

            await handleUpdate({
                entityId: history.entityId,
                id: history.id,
                entityType: history.entityType,
                fieldType: history.fieldType,
                ...dto,
            });

            updateRecordState();
        } catch (e) {
            console.error('History update error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const handleDeleteHistoryRecord = async () => {
        if (!handleDelete) {
            return;
        }

        await handleDelete({ entityId: history.entityId, id: history.id });
    };

    return (
        <HistoryRecordContent
            allClassifiers={allClassifiers}
            classifierCode={classifierCode}
            handleSaveHistoryRecord={handleSaveHistoryRecord}
            handleDeleteHistoryRecord={handleDelete ? handleDeleteHistoryRecord : undefined}
            hasUserEditPermission={hasUserEditPermission}
            history={history}
            index={index}
            recordState={recordState}
            isLatestRecord={isLatestRecord}
            updateRecordState={updateRecordState}
            allHistories={allHistories}
        />
    );
};

export default HistoryRecord;
